import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { ReactComponent as FAVORITE_ICON } from "../../../assets/images/star-icon.svg";
import { useAuth } from "../../../context/AuthContext";
import { launchGameById, launchProviderLobby } from "../../../lib/gameLaunch";
import {
  buildVendorFromFavorite,
  FAVORITES_UPDATED_EVENT,
  getFavoriteGames,
  toggleFavoriteGame,
} from "../../../utils/favoriteGames";
import { getRecentPlayAccountKey } from "../../../utils/recentPlayHistory";
import HomeSectionGameCard from "./HomeSectionGameCard";
import { StyledHomeBackendSections } from "./StyledHomeBackendSections";

const DISPLAY_LIMIT = 12;

const HomeFavoriteSection = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [launchingKey, setLaunchingKey] = useState("");
  const accountKey = useMemo(() => getRecentPlayAccountKey(user), [user]);

  useEffect(() => {
    if (!user?.isAuthenticated || !accountKey) {
      setItems([]);
      return undefined;
    }

    const refreshFavorites = () => {
      setItems(getFavoriteGames(accountKey));
    };

    refreshFavorites();

    const handleFavoritesUpdated = (event) => {
      if (!event?.detail?.accountKey || event.detail.accountKey === accountKey) {
        refreshFavorites();
      }
    };

    const handleStorage = (event) => {
      if (!event.key || event.key.endsWith(`:${accountKey}`)) {
        refreshFavorites();
      }
    };

    window.addEventListener(FAVORITES_UPDATED_EVENT, handleFavoritesUpdated);
    window.addEventListener("storage", handleStorage);

    return () => {
      window.removeEventListener(FAVORITES_UPDATED_EVENT, handleFavoritesUpdated);
      window.removeEventListener("storage", handleStorage);
    };
  }, [accountKey, user]);

  const visibleItems = items.slice(0, DISPLAY_LIMIT);
  const favoriteKeys = useMemo(() => new Set(items.map((item) => item.key)), [items]);

  const handleFavoriteToggle = (item) => {
    const result = toggleFavoriteGame(accountKey, item);
    setItems(result.rows);
  };

  const handleLaunch = async (item) => {
    if (!item) return;

    if (item.href) {
      navigate(item.href);
      return;
    }

    setLaunchingKey(item.key);
    try {
      if (item.kind === "provider") {
        await launchProviderLobby(buildVendorFromFavorite(item), {
          label: item.title,
          gameType: item.gameType,
          meta: item.meta,
        });
        return;
      }

      await launchGameById(item.gameId, {
        label: item.title,
        recentPlay: item,
      });
    } finally {
      setLaunchingKey("");
    }
  };

  if (!user?.isAuthenticated || visibleItems.length === 0) return null;

  return (
    <StyledHomeBackendSections className="home-favorite-wrapper">
      <section className="backend-section favorite-section">
        <div className="backend-section-header">
          <div className="backend-section-title">
            <FAVORITE_ICON className="backend-section-icon" />
            <div>
              <div className="backend-section-name">즐겨찾기</div>
            </div>
          </div>
        </div>

        <div className="backend-card-rail recent-play-rail">
          {visibleItems.map((item, index) => (
            <HomeSectionGameCard
              key={item.key}
              item={item}
              index={index}
              isLaunching={launchingKey === item.key}
              isFavorite={favoriteKeys.has(item.key)}
              onFavoriteToggle={handleFavoriteToggle}
              onLaunch={handleLaunch}
            />
          ))}
        </div>
      </section>
    </StyledHomeBackendSections>
  );
};

export default HomeFavoriteSection;
